// src/features/leases/leaseStatusLabels.js
//
// OFFICE-OVERVIEW-LEASE-STATUS-CONTROL-S1
//
// Canonical shared vocabulary for lease status display and editing.
// Extracted from LeasesListTable.jsx (STATUS_LABELS / STATUS_STYLE_MAP) so the
// Overview header pill, the Edit modal, and the Leases list table all use the
// same labels, keys, and colors.
//
// STATUS VOCABULARY (5 display buckets — matches LeasesListTable)
// ──────────────────────────────────────────────────────────────────────
// The Leases list derives a display bucket from each lease via statusFor()
// (see LeasePortfolioFilters.jsx).  These 5 keys are the canonical
// user-visible vocabulary; they map to underlying DB status column values as
// described in LEASE_BUCKET_TO_DB_STATUS below.
//
//   draft        — lease.status === 'draft'
//   pre_commencement — explicit Committed / raw committed; legacy future-start fallback
//   in_use       — active/holdover/in_negotiation leases at non-vacated locations
//   vacant_held  — active lease at a location with stage === 'vacated' (leased,
//                  empty, NOT sublet — dark space). Renamed from "vacated" to
//                  "vacant_held" (STAGE-VS-LEASE-STATUS-DECOUPLE) so it no
//                  longer collides in label with location.stage's "Vacated"
//                  value — the two axes mean different things (office
//                  lifecycle vs. lease/premises state). Label-only rename;
//                  the underlying condition (active lease, vacated stage) and
//                  the data itself are unchanged — no records were remapped.
//                  (OFFICE-STAGE-STATUS-UNBLOCK) vacant_held is now a
//                  writable option — admins/editors can set it directly;
//                  it maps to DB status 'active'.
//   expired      — lease.status === 'expired'

import { T } from "../../app/tokens.js";

// ── Label map ──────────────────────────────────────────────────────────────
// Single source of truth for display labels. Also consumed by:
//   - LeasesListTable.jsx  (STATUS_LABELS export, status column pill)
//   - LeasesPage.jsx (_LEASE_STATUS_LABEL inline → import from here)
export const LEASE_STATUS_LABELS = {
  draft:             "Draft",
  pre_commencement:  "Committed",
  in_use:            "In-Use",
  vacant_held:       "Vacant-Held",
  expired:           "Expired",
};

// ── Meta map (label + token colors) ───────────────────────────────────────
// Colors match STATUS_STYLE_MAP in LeasesListTable.jsx.
export const LEASE_STATUS_META = {
  draft:             { label: "Draft",            bg: T.buildBlueBg,  color: T.buildBlue,        border: T.buildBlueBorder       },
  // Committed incoming space matches Offices In-Build; separate Outlook styling
  // remains independent of this occupancy-status metadata.
  pre_commencement:  { label: "Committed",        bg: T.buildBlueBg,  color: T.buildBlue,        border: T.buildBlueBorder       },
  // GLOBAL-PILL-STANDARDIZE: canonical positive green = T.ragGreenBg / T.ragGreenText,
  // matching Market Plan "Maintain" pill. Was T.greenSoft / var(--v2-green).
  in_use:            { label: "In-Use",           bg: T.ragGreenBg,   color: T.ragGreenText,      border: "var(--v2-green-border)" },
  vacant_held:       { label: "Vacant-Held",      bg: T.vacatedBg,    color: T.vacatedFg,         border: T.vacatedBorder         },
  expired:           { label: "Expired",          bg: T.lightGray,    color: "var(--v2-light)",   border: "var(--v2-light)"       },
};

// ── Options for editable controls (dropdown / select) ─────────────────────
// All five buckets are writable. `vacant_held` maps to DB status 'active'
// and is appropriate when an admin/editor sets an office to dark-space state
// directly (OFFICE-STAGE-STATUS-UNBLOCK).
export const LEASE_STATUS_OPTIONS = [
  { value: "draft",        label: "Draft"        },
  { value: "pre_commencement", label: "Committed" },
  { value: "in_use",       label: "In-Use"       },
  { value: "vacant_held",  label: "Vacant-Held"  },
  { value: "expired",      label: "Expired"      },
];

// ── DB write-value map ─────────────────────────────────────────────────────
// Maps a derived display-bucket key to the DB `status` column value that will
// be sent in the PATCH body.  Used by update handlers in LocationDetail and
// EditLeaseModal before calling updateLease().
//
// `in_use` maps to 'active' — the canonical DB value for an active,
// non-sublease, non-expired lease at a non-vacated location.
export const LEASE_BUCKET_TO_DB_STATUS = {
  draft:        "draft",
  pre_commencement: "committed",
  in_use:       "active",
  vacant_held:  "active",
  expired:      "expired",
};

// Fallback pill — shown when a lease carries an unrecognised bucket key.
export const LEASE_STATUS_UNKNOWN = { label: "Unknown", bg: T.bg, color: T.mid, border: T.border };

// Empty-state pill — shown when a location is in a lease-bearing stage but
// has no lease record yet (e.g. just moved to Operational, or its prior
// lease was reassigned elsewhere). Distinct from LEASE_STATUS_UNKNOWN, which
// is for a recognised-but-unmapped bucket key on an existing lease.
// (OFFICE-OVERVIEW-LEASE-STATUS-CONTROL-ALL-OFFICES)
export const LEASE_STATUS_EMPTY = { label: "Set lease status…", bg: T.bg, color: T.mid, border: T.border };

// ── Stage applicability ─────────────────────────────────────────────────────
// Stages where the LEASE STATUS control is shown on the Overview header,
// regardless of whether a value is currently set. "closed" (retired, no active
// concern) is excluded — the control never applies to that stage.
// (OFFICE-OVERVIEW-LEASE-STATUS-CONTROL-ALL-OFFICES)
// OFFICE-STAGE-REMOVE-NEGOTIATING-PROSPECTING: negotiating removed from this set.
export const LEASE_BEARING_STAGES = new Set(["operational", "dormant", "build", "vacated"]);
